import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useNavigate,
  useRouteError,
} from '@remix-run/react';
import * as Sentry from '@sentry/react';
import { captureRemixErrorBoundaryError } from '@sentry/remix';
import { RiEmotionSadFill } from 'react-icons/ri';
import './styles/fallback.css';
import './styles/common.css';
import MatomoProvider from './components/MatomoProvider';
import { TeamProvider } from './context/TeamContext';
import { getProfile } from '~/utils/ServerRequest';
import { usePostSignupRedirect } from './hooks/usePostSignupRedirect';
import toast, { Toaster } from 'react-hot-toast';
import { useState, useEffect } from 'react';

export function Layout({ children }: Readonly<{ children: React.ReactNode }>) {
  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
      </head>
      <body>
        <TeamProvider>{children}</TeamProvider>
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export const ErrorBoundary = () => {
  const error = useRouteError();
  captureRemixErrorBoundaryError(error);
  let errorMessage = '';
  let errorStack = '';
  if (error) {
    if (typeof error === 'string') {
      errorMessage = error;
    } else if (typeof error === 'object') {
      if ('message' in error && typeof (error as any).message === 'string') {
        errorMessage = (error as any).message;
      } else {
        errorMessage = JSON.stringify(error);
      }
      if ('stack' in error && typeof (error as any).stack === 'string') {
        errorStack = (error as any).stack;
      }
    }
  }
  return (
    <div style={{ padding: 32 }}>
      <h2>Something went wrong</h2>
      {errorMessage && (
        <pre style={{ color: 'red', whiteSpace: 'pre-wrap' }}>
          {errorMessage}
        </pre>
      )}
      {errorStack && (
        <details>
          <summary>Stack trace</summary>
          <pre style={{ fontSize: 12 }}>{errorStack}</pre>
        </details>
      )}
    </div>
  );
};

import { useLocation } from 'react-router-dom';
const FallbackComponent = (error: any, componentStack: any) => {
  const navigate = useNavigate();
  const location = typeof window !== 'undefined' ? window.location.href : '';
  // Sentry eventId is available via Sentry.lastEventId()
  const sentryEventId = Sentry.lastEventId ? Sentry.lastEventId() : '';
  return (
    <div className="fallbackDiv">
      <RiEmotionSadFill style={{ fontSize: '10vw' }} />
      {/* <img src={logo} width={'100vw'} id="popupLogo" alt="BLUESKEYE AI Logo" /> */}
      {componentStack.toString().includes('QuadrantChart') ? (
        <p>We could not find the video you're looking for.</p>
      ) : (
        <>
          <p>You have encountered an error.</p>
          <p style={{ fontSize: '0.97rem', color: '#555' }}>
            <strong>URL:</strong> {location}
            <br />
            {sentryEventId && (
              <>
                <strong>Sentry Error ID:</strong> {sentryEventId}
                <br />
              </>
            )}
          </p>
        </>
      )}
      <p>
        Please inform the project owner or Blueskeye AI for further assistance.
      </p>
      <p>
        Click{` `}
        <span>
          <button
            id="navigateButton"
            onClick={() => {
              navigate(-1);
            }}
          >
            here
          </button>
        </span>
        {` `}to return to the previous page!
      </p>
    </div>
  );
};

export default function App() {
  const [isClient, setIsClient] = useState(false);
  useEffect(() => {
    setIsClient(true);
  }, []);
  // Global redirect for incomplete signup
  usePostSignupRedirect();

  return (
    <Sentry.ErrorBoundary
      fallback={({ error, componentStack }) =>
        FallbackComponent(error, componentStack)
      }
    >
      {isClient ? (
        <MatomoProvider>
          <Outlet />
        </MatomoProvider>
      ) : (
        <Outlet />
      )}
      <Toaster />
    </Sentry.ErrorBoundary>
  );
}
